/**
 * Materialise a verified semantic snapshot back onto a live exploration.
 *
 * Semantic Git intentionally excludes layout, spend, run receipts, document
 * identity, and revision metadata. Applying a merge therefore replaces only
 * meaning while carrying those local records forward. Existing cards keep their
 * reader-owned folds and stretches; newly merged cards receive normal node
 * defaults and are laid out by the store after this function returns.
 */

import {
  createResearchState,
  normalizeResearchState,
  pruneManualEvidenceBindingsForNodes,
} from './research'
import { archiveVerificationsFor, mergeVerificationArchive } from './rebase'
import {
  admitBridges,
  admitEdges,
  admitEvidenceRebases,
  admitNodeRecords,
  admitProfile,
  admitVerificationArchive,
  admitVerificationChain,
} from './persist'
import { makeNode } from './session'
import {
  SEMANTIC_SNAPSHOT_VERSION,
  semanticBranchHead,
  semanticSnapshotHash,
  semanticWorkspaceAt,
  type SemanticCommit,
  type SemanticNode,
  type SemanticSnapshot,
} from './semanticGit'
import { bridgeReadable, ownEntry, panelLive, type ReasonNode, type Session } from './types'

export class SemanticApplyError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'SemanticApplyError'
  }
}

export class SemanticCheckoutDirtyError extends SemanticApplyError {
  constructor() {
    super('The checked-out branch has uncommitted semantic changes.')
    this.name = 'SemanticCheckoutDirtyError'
  }
}

export type SemanticCheckoutMode = 'refuse-dirty' | 'discard'

export interface SemanticCheckoutResult {
  session: Session
  head: SemanticCommit
  discardedChanges: boolean
}

function materialiseNode(row: SemanticNode, prior: ReasonNode | undefined): ReasonNode {
  const semantic = {
    id: row.claim.id,
    parentId: row.claim.parentId,
    bridgeFrom: row.claim.bridgeFrom,
    // The typed edges, materialised beside the tree edge they extend. Already
    // reconciled against the snapshot's own nodes by `admitSemanticEdges`, so
    // what lands here is what the codec would keep — the comment that used to
    // sit here said the codec re-checks them on the way off disk and treated
    // that as sufficient. It is not: the drop happened at the NEXT read, after
    // the merge commit had already recorded the edge.
    dependsOn: row.claim.dependsOn,
    attacks: row.claim.attacks,
    label: row.claim.label,
    claim: row.claim.claim,
    reasoning: row.claim.reasoning,
    kind: row.claim.kind,
    createdAt: row.claim.createdAt,
    createdOrdinal: row.claim.createdOrdinal,
    editedAt: row.claim.editedAt,
    origin: row.claim.origin,
    evidence: row.evidence.evidence,
    // Through the CODEC's gate, not copied. A merge request is a second door
    // onto this graph and its validator checks a node's shape, so without this a
    // hand-edited file could land a `refuted` reading its sources never earned —
    // and `taint.ts` carries a forged refutation down the whole subtree.
    // Demoted rather than rejected, exactly as a stored document is: the reading
    // becomes `mixed`, `claimedReading` keeps what was claimed, and the sources
    // survive for a reader to judge. See `admitVerificationChain`.
    ...admitVerificationChain(
      row.evidence.verificationHistory,
      row.evidence.verification,
      row.conclusion.novelty,
    ),
    // Already held to the codec's shapes by `admitSemanticSnapshot`, which every
    // path into this function runs first — and which runs on the SNAPSHOT so
    // that the merge commit carries what the graph carries.
    compact: row.claim.compact,
    elaboration: row.claim.elaboration,
    foreignFrom: row.claim.foreignFrom,
    model: row.claim.model,
    evidenceRebases: row.evidence.evidenceRebases,
    audit: row.evidence.audit,
    outcome: row.evidence.outcome,
    survey: row.evidence.survey,
    surveyHistory: row.evidence.surveyHistory,
    resolvesBy: row.conclusion.resolvesBy,
    /**
     * The one record with a rule of its own on top of the codec's, so it stays
     * here where the wording it ruled on is in scope.
     *
     * A panel is CLEARED by a rewrite — the type says so, and `engine/reason.ts`
     * does it on every edit — because it ruled on wording that is no longer
     * there. `verification` and `audit` both survive a rewrite instead and are
     * read through `verificationLive` / `auditLive`, so a stale one of those is
     * visibly stale rather than gone.
     *
     * A field-wise merge can pair one branch's newer `claim` and `editedAt` with
     * the other branch's older `panel` and raise no conflict, because neither
     * side changed the same field. Nothing then cleared it, and every reader
     * treats a panel as current: the candidate earned the `attacked` rung on
     * three lenses that read different words, took panel-agreement weight on the
     * canvas, and sorted and exported above candidates that had actually been
     * attacked.
     *
     * The rule itself now lives in `panelLive`, and the shelf applies it too.
     * This door held it alone for a round, which is the half that made it a
     * finding: the merge dropped the stale panel and reloading the page brought
     * it back.
     */
    panel:
      row.evidence.panel && panelLive(row.evidence.panel.checkedAt, row.claim.editedAt)
        ? row.evidence.panel
        : undefined,
    confidence: row.conclusion.confidence,
    novelty: row.conclusion.novelty,
    tension: row.conclusion.tension,
    nextSteps: row.conclusion.nextSteps,
    terminal: row.conclusion.terminal,
    terminalReason: row.conclusion.terminalReason,
    terminalKind: row.conclusion.terminalKind,
  } satisfies Partial<ReasonNode> & { label: string; claim: string }

  return prior
    ? {
        ...prior,
        ...semantic,
        // A semantic import cannot smuggle in render-time state because none of
        // these fields exists in SemanticNode. The prior local values remain.
        x: prior.x,
        y: prior.y,
        angle: prior.angle,
        radius: prior.radius,
        depth: prior.depth,
        collapsed: prior.collapsed,
        foldAt: prior.foldAt,
        stretch: prior.stretch,
        birth: prior.birth,
        lastPulse: prior.lastPulse,
      }
    : makeNode({ ...semantic, birth: row.claim.createdAt, lastPulse: row.claim.createdAt })
}

function orderedSnapshotIds(session: Session, snapshot: SemanticSnapshot): string[] {
  const present = new Set(Object.keys(snapshot.nodes))
  const carried = session.order.filter((id) => present.delete(id))
  const added = [...present].sort((a, b) => {
    const left = snapshot.nodes[a]!.claim
    const right = snapshot.nodes[b]!.claim
    return (
      (left.createdOrdinal ?? Number.MAX_SAFE_INTEGER) -
        (right.createdOrdinal ?? Number.MAX_SAFE_INTEGER) ||
      left.createdAt - right.createdAt ||
      (a < b ? -1 : a > b ? 1 : 0)
    )
  })
  // PARENT BEFORE CHILD, whatever order the two arrived in.
  //
  // `sanitizeSession` makes one forward pass and drops a node whose parent it
  // has not reached yet — the rule that never restores a dangling edge — and it
  // is safe only because every path that rebuilds `order` puts a parent first.
  // The two lists above do not: an incoming request that REPARENTS a node both
  // sides already had, under one that comes later, leaves the child in the
  // recipient's old position ahead of its new parent. Nothing objects at apply
  // time, because the graph is acyclic and has one root; the node is eaten by
  // the next read, while the head still holds it, so the document comes back
  // short and its own checkout reads dirty.
  //
  // A STABLE lift, not a sort: the candidate order is walked in place and a
  // parent is emitted only when a child forces it, so every node that was
  // already ahead of its parent's chain stays where it was. The ancestor guard
  // is belt-and-braces — `validateTopology` has already refused a cycle — but a
  // hang is a worse way to find that out than a throw.
  const out: string[] = []
  const placed = new Set<string>()
  for (const id of [...carried, ...added]) {
    if (placed.has(id)) continue
    const chain: string[] = []
    const guard = new Set<string>()
    for (let at: string | undefined = id; at && !placed.has(at);) {
      if (guard.has(at)) break
      guard.add(at)
      chain.push(at)
      // `ownEntry`, not `snapshot.nodes[...]`: a `parentId` of `"toString"` finds
      // a prototype member, which is truthy, so the walk stepped onto a function
      // and the next turn read `.claim` off it and threw. A hand-edited request
      // must be REFUSED by `validateTopology` below, not crash before it.
      const parent: string | null = ownEntry(snapshot.nodes, at)?.claim.parentId ?? null
      at = parent && ownEntry(snapshot.nodes, parent) ? parent : undefined
    }
    for (const step of chain.reverse()) {
      if (placed.has(step)) continue
      placed.add(step)
      out.push(step)
    }
  }
  return out
}

/**
 * Reconcile a snapshot's typed edges against its own node set, through the
 * codec's gate. Called by `admitSemanticSnapshot`, which is the door.
 *
 * `validateTopology` REFUSES a snapshot whose `parentId` or `bridgeFrom` names a
 * node that is not there, because a claim with no place in the tree cannot be
 * drawn. A dangling `dependsOn` or `attacks` is not that: the claim is fine and
 * the relation is what is gone, and refusing over it would throw away a
 * colleague's whole merge because they drew an edge to a node this branch
 * deleted. So it is dropped, exactly as `admitBridges` drops a malformed
 * candidate and for the same reason.
 *
 * The property this establishes is agreement between three copies of one graph:
 * the snapshot that gets committed, the session materialised from it, and the
 * document read back off the shelf. They disagreed, and the visible cost was
 * not the missing edge — it was `semanticWorkingTreeDirty` answering true
 * against a commit nobody had edited, which makes a checkout refuse.
 *
 * The snapshot is returned unchanged, by identity, when nothing was dropped.
 */
function admitSemanticEdges(snapshot: SemanticSnapshot): SemanticSnapshot {
  let changed = false
  const nodes: Record<string, SemanticNode> = {}
  for (const [id, row] of Object.entries(snapshot.nodes)) {
    const dependsOn = admitEdges(row.claim.dependsOn, row.claim.id, snapshot.nodes)
    const attacks = admitEdges(row.claim.attacks, row.claim.id, snapshot.nodes)
    const same = sameEdges(dependsOn, row.claim.dependsOn) && sameEdges(attacks, row.claim.attacks)
    if (same) {
      nodes[id] = row
      continue
    }
    changed = true
    const claim = { ...row.claim }
    // Absent rather than empty, because `snapshotNode` omits an empty list and
    // the snapshot is hashed: an `attacks: []` left behind here would be a
    // difference between two people's copies that nobody drew.
    if (dependsOn) claim.dependsOn = dependsOn
    else delete claim.dependsOn
    if (attacks) claim.attacks = attacks
    else delete claim.attacks
    nodes[id] = { ...row, claim }
  }
  return changed ? { ...snapshot, nodes } : snapshot
}

/**
 * THE DOOR. One gate over a whole snapshot, run before anything reads it.
 *
 * It was two half-doors and that is what kept failing. The typed edges were
 * gated on the snapshot, correctly, with a comment on the merge path saying
 * exactly why it has to be the snapshot and not the applied session — and then
 * the per-node records were gated inside `applySemanticSnapshot`, where the
 * commit cannot see them. So a merge that dropped a forged outcome wrote the
 * forgery into its own head commit and the working tree read dirty against it,
 * which is the same three-copies-disagree failure the gate exists to end,
 * reintroduced by the gate.
 *
 * Everything a snapshot carries that this app will later re-read off disk is
 * held here, once, so the committed snapshot, the materialised session and the
 * reloaded document are the same graph by construction.
 */
export function admitSemanticSnapshot(snapshot: SemanticSnapshot): SemanticSnapshot {
  const edged = admitSemanticEdges(snapshot)
  const nodes: Record<string, SemanticNode> = {}
  for (const [id, row] of Object.entries(edged.nodes)) {
    const held = admitNodeRecords({
      compact: row.claim.compact,
      elaboration: row.claim.elaboration,
      parentId: row.claim.parentId,
      bridgeFrom: row.claim.bridgeFrom,
      createdOrdinal: row.claim.createdOrdinal,
      reasoning: row.claim.reasoning,
      evidence: row.evidence.evidence,
      nextSteps: row.conclusion.nextSteps,
      confidence: row.conclusion.confidence,
      novelty: row.conclusion.novelty,
      tension: row.conclusion.tension,
      terminal: row.conclusion.terminal,
      terminalReason: row.conclusion.terminalReason,
      terminalKind: row.conclusion.terminalKind,
      kind: row.claim.kind,
      origin: row.claim.origin,
      createdAt: row.claim.createdAt,
      editedAt: row.claim.editedAt,
      // Left alone HERE, and checked after materialisation instead. A rebase
      // is proven against the completed op it names, and a snapshot carries no
      // ops — so this door cannot ask the question, and dropping every row
      // because a foreign one cannot be proven took a local branch's own
      // receipts with it. `admitEvidenceRebases` asks it where the ops are.
      evidenceRebases: row.evidence.evidenceRebases,
      panel: row.evidence.panel,
      audit: row.evidence.audit,
      outcome: row.evidence.outcome,
      resolvesBy: row.conclusion.resolvesBy,
      foreignFrom: row.claim.foreignFrom,
      model: row.claim.model,
    })
    nodes[id] = {
      ...row,
      claim: {
        ...row.claim,
        ...(held.compact ? { compact: held.compact } : { compact: undefined }),
        elaboration: held.elaboration,
        // Written back before `orderedSnapshotIds` walks parents and before
        // `validateTopology` looks either id up, because both dereference them.
        parentId: held.parentId,
        bridgeFrom: held.bridgeFrom,
        createdOrdinal: held.createdOrdinal,
        reasoning: held.reasoning,
        kind: held.kind,
        origin: held.origin,
        createdAt: held.createdAt,
        editedAt: held.editedAt,
        foreignFrom: held.foreignFrom,
        model: held.model,
      },
      evidence: {
        ...row.evidence,
        evidence: held.evidence,
        evidenceRebases: held.evidenceRebases,
        panel: held.panel,
        audit: held.audit,
        outcome: held.outcome,
      },
      conclusion: {
        ...row.conclusion,
        nextSteps: held.nextSteps,
        confidence: held.confidence,
        novelty: held.novelty,
        tension: held.tension,
        terminalReason: held.terminalReason,
        terminalKind: held.terminalKind,
        resolvesBy: held.resolvesBy,
      },
    }
  }
  return { ...edged, nodes }
}

function sameEdges(kept: string[] | undefined, had: string[] | undefined): boolean {
  if (!kept) return !had || had.length === 0
  return !!had && had.length === kept.length && had.every((id, at) => id === kept[at])
}

function validateTopology(nodes: Record<string, ReasonNode>): void {
  for (const [id, node] of Object.entries(nodes)) {
    if (node.id !== id) throw new SemanticApplyError(`Snapshot node key ${id} names ${node.id}.`)
    // `ownEntry` is what makes "missing" mean missing. A prototype member is
    // truthy, so `nodes[node.parentId]` reported `"toString"` as a node that
    // exists and this refusal — the gate every reader downstream trusts — passed
    // a parent that is not on the graph.
    if (node.parentId !== null && !ownEntry(nodes, node.parentId)) {
      throw new SemanticApplyError(`Snapshot node ${id} has missing parent ${node.parentId}.`)
    }
    for (const parent of node.bridgeFrom ?? []) {
      if (!ownEntry(nodes, parent)) {
        throw new SemanticApplyError(`Snapshot node ${id} has missing synthesis parent ${parent}.`)
      }
    }
  }

  /**
   * And every parent chain reaches a root.
   *
   * "The parent exists" is not "there is a tree": `A.parentId = B` with
   * `B.parentId = A` satisfies the loop above and is exactly what a field-wise
   * merge of two individually acyclic snapshots produces when each reparents one
   * of the pair — no conflict, because neither branch wrote the other's field.
   *
   * The cost is the same three-copies-disagree shape as the dangling edge, one
   * level up and larger: `layout` walks down from the root and never reaches the
   * component, so those cards are drawn nowhere, and `sanitizeSession`'s single
   * forward pass drops every one of them on the next read — "a parent is always
   * inserted before its children" is a promise a cycle cannot keep. A silently
   * invisible subtree today, and a silently deleted one tomorrow.
   *
   * Refused rather than repaired, with `parentId` and `bridgeFrom` above: there
   * is no honest way to pick which of two claims is the other's parent, and
   * guessing would put a person's argument the wrong way round.
   */
  /**
   * And exactly ONE root, not merely one per chain.
   *
   * Round eighteen made every parent chain terminate; this is the other half of
   * the same sentence — that they all terminate at the SAME place. A merge
   * request with no shared commit, from an exploration created independently,
   * makes both node maps additions: two seeds, each with `parentId === null`,
   * and no conflict because neither branch wrote the other's field.
   *
   * The board has one root and every derived reader assumes it. `taintMap` takes
   * `s.order.find((id) => parentId === null)` and treats what it cannot reach
   * from there as unlabelled by design — its own comment calls that the
   * fail-closed boundary for malformed imports — so a refutation anywhere in the
   * second tree propagates to nothing, silently. `layout` walks from one root
   * too, and the codec insists the first node in order is it. The subtree lands,
   * hashes into the commit, and is drawn nowhere.
   *
   * Refused rather than reconciled, with the cycle above and on the same
   * argument: re-parenting somebody's seed under mine invents an attachment
   * nobody drew, in the record built to carry what they did draw. Two unrelated
   * explorations are two boards; the merge that joins them has to say which
   * claim the other now rests on, and only a person can say that.
   */
  const rooted = new Set<string>()
  for (const start of Object.keys(nodes)) {
    if (rooted.has(start)) continue
    const path: string[] = []
    const walking = new Set<string>()
    let at: string | null = start
    while (at !== null && !rooted.has(at)) {
      if (walking.has(at)) {
        throw new SemanticApplyError(`Snapshot node ${at} is its own ancestor.`)
      }
      walking.add(at)
      path.push(at)
      at = ownEntry(nodes, at)?.parentId ?? null
    }
    for (const id of path) rooted.add(id)
  }

  const roots = Object.keys(nodes).filter((id) => nodes[id]!.parentId === null)
  if (roots.length !== 1) {
    // NOT `> 1`, which is what this said first — the same half-a-sentence
    // mistake it was written to correct. Zero roots is the other malformed
    // shape and the louder one: a commit-based request that deletes every node
    // from a shared base merges to an empty map, and applying it replaces a
    // person's board with nothing. `layout` renders it as a blank canvas and
    // the autosave declines to write it (`order.length <= 1`), so the graph is
    // gone from the screen while the last save still holds it — a merge that
    // looks like it worked and has taken everything.
    throw new SemanticApplyError(
      roots.length === 0
        ? 'Snapshot has no root claim. One board has one.'
        : `Snapshot has ${roots.length} root claims (${roots.slice(0, 2).join(', ')}). One board has one.`,
    )
  }
}

/** Apply a merge/import without rewriting operational or reader-owned history. */
export function applySemanticSnapshot(session: Session, incoming: SemanticSnapshot): Session {
  if (incoming.version !== SEMANTIC_SNAPSHOT_VERSION) {
    throw new SemanticApplyError(`Unsupported semantic snapshot version: ${incoming.version}`)
  }

  // Before anything reads the nodes. Every apply path arrives here — but a
  // snapshot that is also COMMITTED must be gated before the commit too, or the
  // live graph and its own head disagree. Idempotent, so both callers may run
  // it. See `applyMergeRequest`.
  const snapshot = admitSemanticSnapshot(incoming)

  const order = orderedSnapshotIds(session, snapshot)
  // The rebase rows, once there are OPS to judge them against — which is here
  // and not at the snapshot door, because a snapshot carries none. A row this
  // device can prove survives a checkout of its own branch; a colleague's row,
  // naming an operation that was never on this device, does not.
  const nodes = admitEvidenceRebases(
    Object.fromEntries(
      order.map((id) => [id, materialiseNode(snapshot.nodes[id]!, session.nodes[id])]),
    ),
    session.ops,
  )
  validateTopology(nodes)

  // Through the codec's own gate, like the verifications above and for the same
  // reason: `validSemanticSnapshot` checks that `bridges` is an object and never
  // looks inside it, so a foreign end with no `ref` reached five readers that
  // dereference `e.ref.label`. `bridgeReadable` below cannot catch that — it
  // asks whether a LOCAL end still names a node, and a malformed foreign end
  // says it is foreign. See `admitBridges`.
  const bridges = admitBridges(Object.values(snapshot.bridges), nodes)
  const unreadable = bridges.find((bridge) => !bridgeReadable(bridge, nodes))
  if (unreadable) {
    throw new SemanticApplyError(`Snapshot bridge ${unreadable.id} names a missing local node.`)
  }

  const baseResearch =
    session.research ??
    createResearchState({ id: `research_${session.id}`, now: session.createdAt })
  const semanticResearch =
    snapshot.research ?? createResearchState({ id: baseResearch.id, now: baseResearch.createdAt })
  const research = pruneManualEvidenceBindingsForNodes(
    normalizeResearchState({
      ...baseResearch,
      evidenceProfile: semanticResearch.evidenceProfile,
      evidenceRecords: semanticResearch.evidenceRecords,
      manualEvidenceBindings: semanticResearch.manualEvidenceBindings ?? [],
      genealogyOverrides: semanticResearch.genealogyOverrides,
      coverageTraces: semanticResearch.coverageTraces,
      evidenceAnnotations: semanticResearch.evidenceAnnotations,
      claimAnnotations: semanticResearch.claimAnnotations,
      decisionCriterionSignals: semanticResearch.decisionCriterionSignals,
      claimScopes: semanticResearch.claimScopes,
      contradictionAnalyses: semanticResearch.contradictionAnalyses,
      falsificationPortfolios: semanticResearch.falsificationPortfolios,
      calibrationTournaments: semanticResearch.calibrationTournaments,
      auditReceipts: semanticResearch.auditReceipts,
      updatedAt: Math.max(baseResearch.updatedAt, Date.now()),
    }),
    new Set(Object.keys(nodes)),
  )
  const removedIds = session.order.filter((id) => !nodes[id])
  const verificationArchive = mergeVerificationArchive(
    // The same door, and the same gate: an archived check reaches the manifest
    // and the compiler even though its claim card is gone.
    admitVerificationArchive(Object.values(snapshot.verificationArchive)),
    session.verificationArchive,
    archiveVerificationsFor(session, removedIds),
  )

  // The profile through the codec's own bounds. `validSemanticProfile` checks
  // the shapes and none of the lengths, so an oversized brief or project label
  // was applied and committed as it arrived and shortened by the next reload —
  // leaving an untouched tree different from the head the merge had just
  // written. See `admitProfile`.
  const profile = admitProfile(snapshot.profile)
  return {
    ...session,
    seed: profile.seed,
    frame: profile.frame,
    brief: profile.brief,
    ...(profile.project === null ? { project: undefined } : { project: profile.project }),
    nodes,
    order,
    bridges,
    verificationArchive,
    nextNodeOrdinal: Math.max(
      session.nextNodeOrdinal ?? 0,
      order.reduce((next, id) => Math.max(next, (nodes[id]?.createdOrdinal ?? -1) + 1), 0),
    ),
    research,
  }
}

/**
 * Compare the live meaning with the revision it says it is editing. Missing or
 * orphaned workspace metadata is dirty by definition: guessing would let edits
 * made on one branch become a commit on another.
 */
/**
 * Whether the live meaning differs from the head it was checked out at.
 *
 * Against the commit's STORED `snapshotHash`, and deliberately not against the
 * head as it would MATERIALISE. Every gate the codec applies on the way off disk
 * — `refutedIsEarned`, the audit's own house, the bounded string fields — can
 * demote a record the head still carries, so a hand-edited document comes back
 * gated live and dirty against its own head. That is a real consequence and it
 * is pinned in the tests; it is left this way because both repairs cost more:
 *
 * Re-gating the embedded snapshot does not fix it, because the comparison is
 * against a stored hash that would then have to be recomputed — and a commit
 * hash is its identity, carried by `parentHash`, `checkedOutCommitHash` and
 * every lineage's `forkedFromCommit`.
 *
 * Hashing `admitSemanticSnapshot(head.snapshot)` instead would read clean, and
 * would lose work. `checkoutSemanticBranch` refuses on dirty precisely to
 * protect uncommitted edits, so a comparison that reads clean more often
 * discards more often — a live edit differing from the head only in what a gate
 * would truncate would go without asking. Dirty is the conservative direction.
 *
 * The way out is the ordinary one: commit the graph as it now stands, which
 * `ResearchLab.tsx` does from the live session.
 */
export async function semanticWorkingTreeDirty(session: Session): Promise<boolean> {
  const research = session.research
  const workspace = research?.semanticWorkspace
  if (!research || !workspace) return true
  const baseline = research.semanticCommits.find(
    (commit) => commit.hash === workspace.checkedOutCommitHash,
  )
  if (!baseline) return true
  return (await semanticSnapshotHash(session)) !== baseline.snapshotHash
}

/**
 * Restore one branch head onto the live Session. The default refuses to discard
 * edits; callers must pass the literal `discard` mode from an explicit user
 * action. Materialisation retains local operational history and archives paid
 * checks on claims absent from the destination snapshot.
 */
export async function checkoutSemanticBranch(
  session: Session,
  targetBranchId: string,
  mode: SemanticCheckoutMode = 'refuse-dirty',
): Promise<SemanticCheckoutResult> {
  const research = session.research
  if (!research) throw new SemanticApplyError('This exploration has no research history.')
  const head = semanticBranchHead(research.semanticCommits, research.branchLineages, targetBranchId)
  if (!head) throw new SemanticApplyError('Commit this branch before checking it out.')

  let dirty: boolean
  if (research.semanticWorkspace) {
    dirty = await semanticWorkingTreeDirty(session)
  } else {
    // A legacy document can safely acquire workspace metadata when its live
    // meaning already is the requested revision. Anything else needs the same
    // explicit discard action as an ordinary dirty checkout.
    dirty = (await semanticSnapshotHash(session)) !== head.snapshotHash
  }
  if (dirty && mode !== 'discard') throw new SemanticCheckoutDirtyError()

  const applied = applySemanticSnapshot(session, head.snapshot)
  const nextResearch = normalizeResearchState({
    ...applied.research!,
    semanticWorkspace: semanticWorkspaceAt(targetBranchId, head.hash),
  })
  return {
    session: { ...applied, research: nextResearch },
    head,
    discardedChanges: dirty,
  }
}
